import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useBack } from '../lib/back.js';
import type { CoverKind, MyShift } from '@keetcheck/shared';
import { theme } from '../theme.js';
import { useAuth } from '../context/AuthContext.js';
import { useMyShifts, useCoverOptions, useCreateCover, useOrgMembers } from '../lib/queries.js';
import { PageShell } from '../components/PageShell.js';
import { Icon } from '../components/Icon.js';
import { CapabilityStrip } from '../components/CoverBits.js';
import { KIND_LABEL, rangeLabel, dayLabel, timeRange, clockWithin, hhmm } from '../lib/shifts.js';

type Mode = 'whole' | 'part';

/**
 * "Can't make it": pick the shift(s) — or just some hours — see what the crew
 * loses and who'd be best to ask, then send. Opened from My shifts with
 * ?shift=<id>.
 */
export function CoverNewPage() {
  const nav = useNavigate();
  const back = useBack('/shifts');
  const [params] = useSearchParams();
  const { user } = useAuth();
  const orgId = user?.orgId ?? '';
  // ?for=<userId>: a leader/admin asking on a member's behalf.
  const forUser = params.get('for');
  const { data: mine = [] } = useMyShifts(forUser);
  const { data: members = [] } = useOrgMembers(forUser ? orgId : '');
  const forMember = forUser ? members.find((m) => m.userId === forUser) : undefined;
  const first = mine.find((s) => s.id === params.get('shift'));

  // Following shifts of the same kind (e.g. the rest of the weekend), not already asked about.
  const following = useMemo(() => !first ? [] : mine.filter((s) =>
    s.kind === first.kind && s.as === first.as && s.startsAt > first.startsAt && !s.myCover
    && new Date(s.startsAt).getTime() - new Date(first.startsAt).getTime() < 7 * 86400_000,
  ), [mine, first]);

  const [extra, setExtra] = useState<Set<string>>(new Set());
  const [mode, setMode] = useState<Mode>('whole');
  const [from, setFrom] = useState<string>('');
  const [to, setTo] = useState<string>('');
  const [note, setNote] = useState('');
  const create = useCreateCover(orgId);

  const chosen: MyShift[] = first ? [first, ...following.filter((s) => extra.has(s.id))] : [];
  const last = chosen[chosen.length - 1];
  let startsAt = first?.startsAt ?? null, endsAt = last?.endsAt ?? null;
  if (first && mode === 'part' && chosen.length === 1) {
    startsAt = (from && clockWithin(from, first.startsAt, first.endsAt)) || first.startsAt;
    endsAt = (to && clockWithin(to, startsAt, first.endsAt)) || first.endsAt;
  }
  const kind = (first?.kind ?? null) as CoverKind | null;
  const { data: opts, isFetching, isPlaceholderData } = useCoverOptions(orgId, kind, startsAt, endsAt, forUser);

  if (!first || !startsAt || !endsAt || !kind) {
    return (
      <PageShell noNav>
        <Header onBack={back} />
        <div style={{ padding: 24, color: theme.textMuted, fontSize: 13 }}>
          {mine.length ? `That shift isn't ${forUser ? 'theirs' : 'one of yours'} any more.` : 'Loading…'}
        </div>
      </PageShell>
    );
  }

  async function send() {
    const r = await create.mutateAsync({ kind: kind!, startsAt: startsAt!, endsAt: endsAt!, note: note.trim() || null, ...(forUser ? { forUserId: forUser } : {}) });
    nav(`/cover/${r.id}`, { replace: true });
  }

  return (
    <PageShell noNav>
      <Header onBack={back} />
      <div style={{ flex: 1, overflowY: 'auto', background: theme.bg, padding: '14px 16px 32px', display: 'flex', flexDirection: 'column', gap: 14 }}>
        {forUser && (
          <div style={{ ...banner, background: theme.surface2, color: theme.textDim }}>
            Asking for cover for <strong style={{ color: theme.text }}>{forMember?.displayName ?? 'a member'}</strong>. You'll get the updates
            {forMember && !forMember.hasApp ? ' (they don’t use the app)' : ''}.
          </div>
        )}
        <Card>
          <div style={label}>{KIND_LABEL[first.kind]}{first.teamName ? ` · ${first.teamName}` : ''}</div>
          <div style={{ fontSize: 17, fontWeight: 800, color: theme.text }}>{rangeLabel(startsAt, endsAt)}</div>
          {chosen.length === 1 && (
            <div role="radiogroup" style={{ display: 'flex', gap: 6, marginTop: 10 }}>
              {(['whole', 'part'] as const).map((m) => (
                <button key={m} role="radio" aria-checked={mode === m} onClick={() => setMode(m)} style={chip(mode === m)}>
                  {m === 'whole' ? 'Whole shift' : 'Just some hours'}
                </button>
              ))}
            </div>
          )}
          {mode === 'part' && chosen.length === 1 && (
            <div style={{ display: 'flex', gap: 10, marginTop: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              <label style={timeLabel}>From <input type="time" value={from || hhmm(first.startsAt)} onChange={(e) => setFrom(e.target.value)} style={input} /></label>
              <label style={timeLabel}>to <input type="time" value={to || hhmm(first.endsAt)} onChange={(e) => setTo(e.target.value)} style={input} /></label>
            </div>
          )}
          {following.length > 0 && (
            <div style={{ marginTop: 12 }}>
              <div style={{ ...label, marginBottom: 6 }}>Also can't make</div>
              {following.slice(0, 10).map((s) => (
                <label key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: theme.text, padding: '4px 0' }}>
                  <input type="checkbox" checked={extra.has(s.id)} onChange={() => {
                    const next = new Set(extra);
                    if (next.has(s.id)) next.delete(s.id); else next.add(s.id);
                    setExtra(next); setMode('whole');
                  }} />
                  {dayLabel(s.startsAt)} · {timeRange(s.startsAt, s.endsAt)}
                </label>
              ))}
            </div>
          )}
        </Card>

        {opts && !opts.valid && <div style={{ ...banner, background: theme.status.danger.bg, color: theme.status.danger.fg }}>{opts.error}</div>}

        {opts?.valid && opts.impact && (
          <Card>
            <div style={label}>If nobody covers</div>
            <div style={{ fontSize: 14, fontWeight: 600, color: theme.text, marginBottom: opts.impact.after ? 8 : 0 }}>{opts.impact.summary}</div>
            {opts.impact.after && <CapabilityStrip cap={opts.impact.after} />}
          </Card>
        )}

        {opts?.valid && (
          <Card>
            <div style={label}>Who's free{isFetching ? ' …' : ''}</div>
            {opts.impact?.after && opts.impact.after.warnings.length === 0 && (
              <div style={{ fontSize: 12, color: theme.textMuted, marginBottom: 6 }}>
                {opts.impact.teamName ?? 'The crew'} keeps {opts.impact.after.count} with every skill covered — any cover is a bonus.
              </div>
            )}
            {opts.candidates.length === 0 && <div style={{ fontSize: 13, color: theme.textMuted }}>Nobody eligible is free then. Send it anyway — someone may swap things around.</div>}
            {opts.candidates.slice(0, 6).map((c) => (
              <div key={c.userId} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', borderTop: `1px solid ${theme.border}` }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: theme.text }}>
                    {c.displayName} <span style={{ fontWeight: 500, color: theme.textMuted, fontSize: 12 }}>{c.teamName ?? ''}{c.hasApp ? '' : ' · no app — call them'}</span>
                  </div>
                  <div style={{ fontSize: 12, color: c.needed ? theme.status.service.fg : theme.textMuted }}>
                    {[...c.reasons, c.free === 'part' ? 'free for part of it' : null, c.coveredYou ? `covered ${forUser ? (forMember?.displayName.split(' ')[0] ?? 'them') : 'you'} ${c.coveredYou}× this year` : null].filter(Boolean).join(' · ') || 'free'}
                  </div>
                </div>
              </div>
            ))}
            {opts.candidates.length > 6 && <div style={{ fontSize: 12, color: theme.textMuted, paddingTop: 4 }}>+ {opts.candidates.length - 6} more</div>}
            <div style={{ fontSize: 12, color: theme.textMuted, marginTop: 8 }}>Everyone who's free and uses the app gets a notification. You can nudge someone directly afterwards.</div>
          </Card>
        )}

        <textarea
          value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} rows={2}
          placeholder="Note for whoever covers (optional)" aria-label="Note"
          style={{ ...input, width: '100%', resize: 'vertical', fontSize: 14, flexShrink: 0, minHeight: 60 }}
        />
        {create.error && <div style={{ ...banner, background: theme.status.danger.bg, color: theme.status.danger.fg }}>{(create.error as Error).message}</div>}
        <button disabled={!opts?.valid || isPlaceholderData || create.isPending} onClick={() => void send()} style={{
          padding: '13px 0', borderRadius: 12, border: 'none', fontSize: 15, fontWeight: 700, fontFamily: theme.font,
          background: theme.accent, color: theme.accentInk, cursor: 'pointer', opacity: !opts?.valid ? 0.5 : 1,
        }}>
          {create.isPending ? 'Sending…' : 'Ask for cover'}
        </button>
      </div>
    </PageShell>
  );
}

function Header({ onBack }: { onBack: () => void }) {
  return (
    <div style={{
      padding: '12px 16px', background: theme.surface, borderBottom: `1px solid ${theme.border}`,
      display: 'flex', alignItems: 'center', gap: 10, position: 'sticky', top: 0, zIndex: 5,
    }}>
      <button onClick={onBack} aria-label="Back" style={iconBtn}><Icon name="back" size={18} /></button>
      <div style={{ fontSize: 17, fontWeight: 700, color: theme.text }}>Can't make it</div>
    </div>
  );
}

function Card({ children }: { children: React.ReactNode }) {
  return <div style={{ padding: '12px 14px', borderRadius: 12, background: theme.surface, border: `1px solid ${theme.border}`, flexShrink: 0 }}>{children}</div>;
}

const label: React.CSSProperties = { fontSize: 11, fontWeight: 700, letterSpacing: 0.3, textTransform: 'uppercase', color: theme.textMuted, marginBottom: 4 };
const timeLabel: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: theme.textDim };
const input: React.CSSProperties = {
  padding: '8px 10px', borderRadius: 8, border: `1px solid ${theme.border}`, background: theme.surface,
  color: theme.text, fontFamily: theme.font, fontSize: 14, boxSizing: 'border-box',
};
const banner: React.CSSProperties = { padding: '10px 12px', borderRadius: 10, fontSize: 13 };
const iconBtn: React.CSSProperties = {
  width: 36, height: 36, border: `1px solid ${theme.border}`, background: theme.surface2, color: theme.text,
  borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
};
const chip = (on: boolean): React.CSSProperties => ({
  padding: '6px 12px', borderRadius: 999, fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: theme.font,
  border: `1px solid ${on ? theme.accent : theme.border}`, background: on ? theme.accent : theme.surface,
  color: on ? theme.accentInk : theme.textDim,
});
